import Papa from "papaparse";
import * as XLSX from "xlsx";

export type ContactBatchExportFormat = "CSV" | "XLSX" | "PDF";

export interface ContactBatchExportRow {
  contact_id?: string;
  name: string;
  phone_number: string;
  meta: Record<string, string>;
}

export interface ContactBatchExportRequest {
  format: ContactBatchExportFormat;
  batchName: string;
  metaCols: string[];
  rows: ContactBatchExportRow[];
}

const NAME_HEADER = "Name";
const PHONE_HEADER = "Phone Number";
const CONTACT_ID_HEADER = "Contact ID";

function formatDateStamp(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}${month}${day}`;
}

function safeFilename(value: string) {
  return value.trim().replace(/[^a-zA-Z0-9_-]+/g, "_").replace(/^_+|_+$/g, "") || "contacts";
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function headersFor(metaCols: string[]) {
  return [CONTACT_ID_HEADER, NAME_HEADER, PHONE_HEADER, ...metaCols];
}

function tableAoa(request: ContactBatchExportRequest): string[][] {
  const headers = headersFor(request.metaCols);
  const body = request.rows.map((row) => [
    row.contact_id ?? "",
    row.name ?? "",
    row.phone_number ?? "",
    ...request.metaCols.map((key) => row.meta?.[key] ?? ""),
  ]);
  return [headers, ...body];
}

export function contactBatchExportFilename(request: ContactBatchExportRequest) {
  return `${safeFilename(request.batchName)}_contacts_${formatDateStamp()}.${request.format.toLowerCase()}`;
}

export async function downloadContactBatchExport(request: ContactBatchExportRequest) {
  if (request.rows.length === 0) {
    throw new Error("Add at least one row before exporting.");
  }

  const filename = contactBatchExportFilename(request);
  const aoa = tableAoa(request);

  if (request.format === "CSV") {
    const csv = Papa.unparse(aoa);
    saveBlob(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }), filename);
    return filename;
  }

  if (request.format === "XLSX") {
    const sheet = XLSX.utils.aoa_to_sheet(aoa);
    sheet["!cols"] = aoa[0].map((_, col) => {
      const width = aoa.reduce((max, row) => Math.max(max, String(row[col] ?? "").length), 8);
      return { wch: Math.min(40, Math.max(12, width + 2)) };
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Contacts");
    XLSX.writeFile(workbook, filename);
    return filename;
  }

  saveBlob(new Blob([buildContactsPdf(request.batchName, aoa)], { type: "application/pdf" }), filename);
  return filename;
}

/** Landscape A4 table PDF. Helvetica only — non-latin glyphs become "?". */
function buildContactsPdf(title: string, aoa: string[][]): ArrayBuffer {
  const page = { width: 842, height: 595 };
  const margin = 36;
  const headerBand = 52;
  const rowH = 16;
  const fontSize = 8;
  const titleSize = 12;
  const innerWidth = page.width - margin * 2;
  const headers = aoa[0] ?? [];
  const data = aoa.slice(1);
  const colCount = Math.max(headers.length, 1);

  const weights = headers.map((h, i) => {
    const sample = data.slice(0, 40).reduce((max, row) => Math.max(max, String(row[i] ?? "").length), String(h).length);
    return Math.max(8, Math.min(28, sample));
  });
  const weightSum = weights.reduce((a, b) => a + b, 0) || 1;
  const colW = weights.map((w) => (w / weightSum) * innerWidth);

  const rowsPerPage = Math.max(1, Math.floor((page.height - margin - headerBand - 18) / rowH) - 1);
  const pageCount = Math.max(1, Math.ceil(data.length / rowsPerPage));

  const pageStreams: string[] = [];
  for (let p = 0; p < pageCount; p++) {
    const slice = data.slice(p * rowsPerPage, (p + 1) * rowsPerPage);
    const cmds: string[] = [];
    cmds.push("BT");
    cmds.push(`/F2 ${titleSize} Tf`);
    cmds.push(`${margin} ${page.height - 28} Td`);
    cmds.push(`(${pdfString(title)}) Tj`);
    cmds.push("ET");
    cmds.push("BT");
    cmds.push(`/F1 8 Tf`);
    cmds.push(`0.45 0.45 0.45 rg`);
    cmds.push(`${margin} ${page.height - 42} Td`);
    cmds.push(`(${pdfString(`${data.length} row${data.length === 1 ? "" : "s"}  ·  page ${p + 1} of ${pageCount}`)}) Tj`);
    cmds.push("ET");
    cmds.push("0 0 0 rg");

    const tableTop = page.height - headerBand;
    drawRow(cmds, headers, colW, margin, tableTop, rowH, fontSize, true);
    slice.forEach((row, i) => {
      drawRow(cmds, row, colW, margin, tableTop - rowH * (i + 1), rowH, fontSize, false);
    });
    pageStreams.push(cmds.join("\n"));
  }

  return assemblePdf(page, pageStreams);
}

function drawRow(
  cmds: string[],
  cells: string[],
  colW: number[],
  x0: number,
  top: number,
  rowH: number,
  fontSize: number,
  header: boolean,
) {
  const y = top - rowH;
  if (header) {
    cmds.push("0.96 0.38 0.13 rg");
    cmds.push(`${x0} ${y} ${colW.reduce((a, b) => a + b, 0)} ${rowH} re f`);
    cmds.push("1 1 1 rg");
  } else {
    cmds.push("0.92 0.90 0.88 RG");
    cmds.push("0.4 w");
    cmds.push(`${x0} ${y} ${colW.reduce((a, b) => a + b, 0)} ${rowH} re S`);
    cmds.push("0.15 0.15 0.15 rg");
  }

  let x = x0;
  cmds.push("BT");
  cmds.push(`/${header ? "F2" : "F1"} ${fontSize} Tf`);
  for (let i = 0; i < colW.length; i++) {
    const maxChars = Math.max(4, Math.floor(colW[i] / (fontSize * 0.5)) - 1);
    const text = fitCell(String(cells[i] ?? ""), maxChars);
    cmds.push(`1 0 0 1 ${x + 4} ${y + 5} Tm`);
    cmds.push(`(${pdfString(text)}) Tj`);
    x += colW[i];
  }
  cmds.push("ET");
  cmds.push("0 0 0 rg");
}

function fitCell(value: string, maxChars: number) {
  const clean = value.replace(/\s+/g, " ").trim();
  if (clean.length <= maxChars) return clean;
  return clean.slice(0, Math.max(1, maxChars - 1)) + "…";
}

function winAnsi(value: string) {
  let out = "";
  for (const ch of value) {
    const c = ch.codePointAt(0) ?? 32;
    if (c === 0x2018 || c === 0x2019) {
      out += "'";
      continue;
    }
    if (c === 0x201c || c === 0x201d) {
      out += '"';
      continue;
    }
    if (c === 0x2013 || c === 0x2014 || c === 0x2212) {
      out += "-";
      continue;
    }
    if (c === 0x2026) {
      out += "...";
      continue;
    }
    if (c === 9 || c === 10 || c === 13) {
      out += " ";
      continue;
    }
    if (c < 32) continue;
    if (c === 127) continue;
    if (c < 127 || (c >= 160 && c <= 255)) {
      out += String.fromCharCode(c);
      continue;
    }
    out += "?";
  }
  return out;
}

function pdfString(value: string) {
  return winAnsi(value).replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");
}

function assemblePdf(page: { width: number; height: number }, streams: string[]): ArrayBuffer {
  const objects: string[] = [];
  const catalogId = 1;
  const pagesId = 2;
  const fontId = 3;
  const fontBoldId = 4;
  const firstPageId = 5;

  const pageIds: number[] = [];
  const contentIds: number[] = [];
  streams.forEach((_, i) => {
    pageIds.push(firstPageId + i * 2);
    contentIds.push(firstPageId + i * 2 + 1);
  });

  objects[catalogId] = `<< /Type /Catalog /Pages ${pagesId} 0 R >>`;
  objects[pagesId] = `<< /Type /Pages /Count ${streams.length} /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] >>`;
  objects[fontId] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>";
  objects[fontBoldId] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>";

  streams.forEach((stream, i) => {
    objects[pageIds[i]] =
      `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${page.width} ${page.height}] ` +
      `/Resources << /Font << /F1 ${fontId} 0 R /F2 ${fontBoldId} 0 R >> >> ` +
      `/Contents ${contentIds[i]} 0 R >>`;
    objects[contentIds[i]] = `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`;
  });

  let body = "%PDF-1.4\n";
  const offsets: number[] = [0];
  for (let id = 1; id < objects.length; id++) {
    offsets[id] = body.length;
    body += `${id} 0 obj\n${objects[id]}\nendobj\n`;
  }
  const xrefAt = body.length;
  const lines = [`xref`, `0 ${objects.length}`, `0000000000 65535 f `];
  for (let id = 1; id < objects.length; id++) {
    lines.push(`${String(offsets[id]).padStart(10, "0")} 00000 n `);
  }
  body += lines.join("\n") + "\n";
  body += `trailer\n<< /Size ${objects.length} /Root ${catalogId} 0 R >>\nstartxref\n${xrefAt}\n%%EOF\n`;

  const bytes = new Uint8Array(body.length);
  for (let i = 0; i < body.length; i++) bytes[i] = body.charCodeAt(i) & 0xff;
  return bytes.buffer;
}
